import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';

import axiosInstance from './axios';
import { getConfig } from './config';
import { getQueryClient } from './query-client';
import {
  APIResponse,
  Contact,
  EditPaymentV2Payload,
  MemberByLocation,
  Membership,
  MemberProfile,
  MemberSummary,
  PaymentDetail,
  PaymentHistory,
  PaymentMethod,
  PaymentHistoryV2,
  TakePaymentV2Payload,
  UpdatePaymentTypeOptions,
  UpdatePaymentV2StatusPayload,
  CreateSimulatedMembershipResponse,
  Invoice,
  CreateInvoicePayload,
  TakePaymentV2Response,
  CreatePreCCPaymentPayload,
  CreatePreCCPaymentResponse,
  UpdateCCPaymentPayload,
  CreateACHPaymentPayload,
  CreateSimulatedMembershipPayload,
  SetAsPrimaryPaymentPayload,
  ActivityHistory,
} from './types';
import { NewContact } from '~/modules/contact/components/ContactCreation/helpers/contactCreationSchema';
import { membership as membershipKeys } from './membership/membership.keys';

export const getMembersByLocation = <TData>(
  {
    locationId,
    name,
    memberId,
    registered,
  }: {
    locationId: number;
    name?: string;
    memberId?: number;
    registered?: number;
  },
  signal?: any,
) => {
  const config = getConfig();

  return axiosInstance
    .get<APIResponse<TData>>(`${config.domains.members}/members/locations/${locationId}/members`, {
      params: {
        name,
        member_id: memberId,
        registered: registered ?? 0,
      },
      signal,
    })
    .then(({ data }) => data?.data);
};

export const useGetMembersByLocationQuery = <TData = MemberSummary[]>(params: {
  locationId: number;
  registered?: number;
}) =>
  useQuery({
    queryKey: ['members', params],
    queryFn: () => getMembersByLocation<TData>(params),
    refetchInterval: params.registered === 1 ? 5000 : false,
  });

export const useGetMembersByNameQuery = <TData = MemberByLocation>(params: {
  locationId: number;
  name?: string;
  memberId?: number;
  registered?: number;
}) =>
  useQuery({
    queryKey: ['members', params],
    queryFn: ({ signal }: any) => getMembersByLocation<TData[]>(params, signal),
    enabled: Boolean(params.name) || Boolean(params.memberId),
  });

export const getMembersByLocationV2 = <TData>(
  {
    locationId,
    name,
    memberId,
    registered,
  }: {
    locationId: number;
    name?: string;
    memberId?: number;
    registered?: number;
  },
  signal?: any,
) => {
  const config = getConfig();

  return axiosInstance
    .get<APIResponse<TData>>(`${config.domains.membersV2}/members/locations/${locationId}/members`, {
      params: {
        name,
        member_id: memberId,
        registered: registered ?? 0,
      },
      signal,
    })
    .then(({ data }) => data?.data);
};
export const useGetMembersByLocationV2Query = <TData = MemberSummary[]>(params: {
  locationId: number;
  registered?: number;
}) =>
  useQuery({
    queryKey: ['membersv2', params],
    queryFn: () => getMembersByLocationV2<TData>(params),
    refetchInterval: params.registered === 1 ? 5000 : false,
  });

export const useGetMembersByNameV2Query: any = (params: {
  locationId: number;
  name?: string;
  memberId?: number;
  registered?: number;
}) =>
  useQuery({
    queryKey: ['membersv2', params],
    queryFn: ({ signal }: any) => getMembersByLocationV2(params, signal),
    enabled: Boolean(params.name) || Boolean(params.memberId),
  });

export const getContactUs = <TData>({ locationId }: { locationId: number }) => {
  const config = getConfig();

  return axiosInstance
    .get<APIResponse<TData>>(`${config.domains.membersV2}/members/locations/${locationId}/contact_us`)
    .then(({ data }) => data?.data);
};
export const useGetContactUsQuery = <TData = MemberSummary[]>(params: { locationId: number }) =>
  useQuery({
    queryKey: ['contact_us', params],
    queryFn: () => getContactUs<TData>(params),
  });

const createMember = ({ locationId }: { locationId: number }, member: NewContact) => {
  const config = getConfig();

  return axiosInstance.post<APIResponse>(`${config.domains.members}/members/locations/${locationId}/members`, member);
};
export const useCreateMemberMutation: any = (params: { locationId: number }) =>
  useMutation({
    mutationFn: (member: NewContact) => createMember(params, member),
    onSuccess: async (response: any) => {
      const queryClient = getQueryClient();

      await queryClient.refetchQueries({ queryKey: ['members', params] });
      if (response.data.status === 'CONFLICT') {
        toast.error(response.data.message);
      } else {
        toast.success(response.data.message);
      }
    },
  });

const createMemberV2 = ({ locationId }: { locationId: number }, member: NewContact) => {
  const config = getConfig();

  return axiosInstance.post<APIResponse>(`${config.domains.membersV2}/members/locations/${locationId}/members`, member);
};
export const useCreateMemberV2Mutation: any = (params: { locationId: number }) =>
  useMutation({
    mutationFn: (member: NewContact) => createMemberV2(params, member),
    onSuccess: async (response: any) => {
      const queryClient = getQueryClient();

      await queryClient.refetchQueries({ queryKey: ['membersv2', params] });
      if (response.data.status === 'CONFLICT') {
        toast.error(response.data.message);
      } else {
        toast.success(response.data.message);
      }
    },
  });

const setupEmail = ({ locationId, memberId }: { locationId: number; memberId: number }) => {
  const config = getConfig();

  return axiosInstance.post<APIResponse>(
    `${config.domains.members}/members/locations/${locationId}/members/${memberId}/setup-email`,
  );
};

export const useSetupEmailMutation: any = (params: { locationId: number; memberId: number }) =>
  useMutation({
    mutationFn: () => setupEmail(params),
    onSuccess: async (response: any) => {
      toast.success(response.data.message);
    },
  });

export const getPaymentMethods = ({ locationId, memberId }: { locationId: number; memberId: number }) => {
  const config = getConfig();

  return axiosInstance
    .get<
      APIResponse<PaymentMethod[]>
    >(`${config.domains.members}/members/locations/${locationId}/members/${memberId}/payment-methods`)
    .then(({ data }) => data?.data);
};
export const useGetPaymentMethodsQuery = (params: { locationId: number; memberId: number | undefined }) =>
  useQuery({
    queryKey: ['payment-methods', params],
    queryFn: () => getPaymentMethods({ ...params, memberId: params.memberId! }),
    enabled: !!params.locationId && !!params.memberId,
  });

export const getPaymentMethodsV2 = ({ locationId, memberId }: { locationId: number; memberId: number }) => {
  const config = getConfig();

  return axiosInstance
    .get<
      APIResponse<PaymentMethod[]>
    >(`${config.domains.membersV2}/members/locations/${locationId}/members/${memberId}/payment-methods`)
    .then(({ data }) => data?.data);
};
export const useGetPaymentMethodsV2Query = (params: { locationId: number; memberId: number | undefined }) =>
  useQuery({
    queryKey: ['payment-methodsV2', params],
    queryFn: () => getPaymentMethodsV2({ ...params, memberId: params.memberId! }),
    enabled: typeof params.locationId === 'number' && typeof params.memberId === 'number',
  });

export const getPaymentMethod = ({
  locationId,
  memberId,
  paymentMethodId,
}: {
  locationId: number;
  memberId: number;
  paymentMethodId: number;
}) => {
  const config = getConfig();

  if (!paymentMethodId) return new Promise((resolve) => resolve({}));

  return axiosInstance
    .get(
      `${config.domains.members}/members/locations/${locationId}/members/${memberId}/payment-methods/${paymentMethodId}`,
    )
    .then(({ data }) => data?.data);
};
export const useGetPaymentMethodQuery: any = (params: {
  locationId: number;
  memberId: number;
  paymentMethodId: number;
}) =>
  useQuery({
    queryKey: ['payment-method', params],
    queryFn: () => getPaymentMethod(params),
    enabled: !!params.locationId && !!params.memberId && !!params.paymentMethodId,
  });

export const getPaymentMethodV2 = ({
  locationId,
  memberId,
  paymentMethodId,
}: {
  locationId: number;
  memberId: number;
  paymentMethodId: number;
}) => {
  const config = getConfig();

  if (!paymentMethodId) return new Promise((resolve) => resolve({}));

  return axiosInstance
    .get(
      `${config.domains.membersV2}/members/locations/${locationId}/members/${memberId}/payment-methods/${paymentMethodId}`,
    )
    .then(({ data }) => data?.data);
};
export const useGetPaymentMethodV2Query = (params: {
  locationId: number;
  memberId?: number;
  paymentMethodId: number | undefined;
}) =>
  useQuery({
    queryKey: ['payment-methodV2', params],
    queryFn: () => {
      const { locationId, memberId, paymentMethodId } = params;
      return getPaymentMethodV2({ locationId, memberId: memberId!, paymentMethodId: paymentMethodId! });
    },
    enabled: !!params.locationId && !!params.memberId && !!params.paymentMethodId,
  });

const updatePaymentMethod = (
  { locationId, memberId, paymentMethodId }: { locationId: number; memberId: number; paymentMethodId: number },
  request: any,
) => {
  const config = getConfig();

  return axiosInstance
    .put<
      APIResponse<PaymentMethod>
    >(`${config.domains.members}/members/locations/${locationId}/members/${memberId}/payment-methods/${paymentMethodId}`, request)
    .then(({ data }) => data);
};
export const useUpdatePaymentMethodMutation = (params: { locationId: number }) =>
  useMutation({
    mutationFn: ({ request, paymentMethodId }: any) =>
      updatePaymentMethod({ ...params, memberId: request.member_id, paymentMethodId }, request),
    onSuccess: async (response: APIResponse<PaymentMethod>) => {
      toast.success(response.message);

      const queryClient = getQueryClient();

      queryClient.invalidateQueries({
        queryKey: ['payment-methods', { locationId: params.locationId, memberId: response.data.member_id }],
      });

      queryClient.invalidateQueries({
        queryKey: [
          'payment-method',
          { locationId: params.locationId, memberId: response.data.member_id, paymentMethodId: response.data.id },
        ],
      });
    },
  });

const deletePaymentMethod = ({
  locationId,
  memberId,
  paymentMethodId,
}: {
  locationId: number;
  memberId: number;
  paymentMethodId: number;
}) => {
  const config = getConfig();

  return axiosInstance.delete<APIResponse>(
    `${config.domains.members}/members/locations/${locationId}/members/${memberId}/payment-methods/${paymentMethodId}`,
  );
};
export const useDeletePaymentMethodMutation: any = (params: {
  locationId: number;
  memberId: number;
  paymentMethodId: number;
}) =>
  useMutation({
    mutationFn: () => deletePaymentMethod({ ...params }),
    onSuccess: async (response: any) => {
      const queryClient = getQueryClient();

      await queryClient.refetchQueries({
        queryKey: ['payment-methods', { locationId: params.locationId, memberId: params.memberId }],
      });
      toast.success(response.data.message);
    },
  });

const createCCPayment = ({ locationId, memberId }: { locationId: number; memberId: number }, request: any) => {
  const config = getConfig();

  return axiosInstance
    .post<
      APIResponse<CreatePreCCPaymentResponse>
    >(`${config.domains.members}/members/locations/${locationId}/members/${memberId}/payment-methods/card`, request)
    .then(({ data }) => data);
};
export const useCreateCCPaymentMutation = (params: { locationId: number }) =>
  useMutation({
    mutationFn: (request: CreatePreCCPaymentPayload) =>
      createCCPayment({ ...params, memberId: request.member_id }, request),
    onSuccess: async (response: APIResponse<CreatePreCCPaymentResponse>) => {
      toast.success(response.message);

      const queryClient = getQueryClient();

      queryClient.refetchQueries({
        queryKey: ['payment-methods', { locationId: params.locationId, memberId: response.data.member_id }],
      });

      queryClient.refetchQueries({
        queryKey: [
          'payment-method',
          { locationId: params.locationId, memberId: response.data.member_id, paymentMethodId: response.data.id },
        ],
      });
    },
  });

const createACHPayment = ({ locationId, memberId }: { locationId: number; memberId: number }, request: any) => {
  const config = getConfig();

  return axiosInstance
    .post<
      APIResponse<PaymentMethod>
    >(`${config.domains.members}/members/locations/${locationId}/members/${memberId}/payment-methods/ach`, request)
    .then(({ data }) => data);
};

export const useCreateACHPaymentMutation = (params: { locationId: number }) =>
  useMutation({
    mutationFn: (request: CreateACHPaymentPayload) =>
      createACHPayment({ ...params, memberId: request.member_id }, request),
    onSuccess: async (response: APIResponse<PaymentMethod>, variables) => {
      toast.success(response.message);

      const queryClient = getQueryClient();

      queryClient.invalidateQueries({
        queryKey: ['payment-methods', { locationId: params.locationId, memberId: response.data.member_id }],
      });

      queryClient.setQueryData(
        ['payment-methods', { locationId: response.data.location_id, memberId: response.data.member_id }],
        (currentPaymentMethodList: PaymentMethod[]) =>
          currentPaymentMethodList
            .map((list) => ({ ...list, default: false }))
            .concat({
              ...response.data,
              default: true,
              last_4_digits_account: variables.account_ach.substring(variables.account_ach.length - 4),
              last_4_digits_routing: variables.routing_ach.substring(variables.routing_ach.length - 4),
            }),
      );

      queryClient.refetchQueries({
        queryKey: [
          'payment-method',
          { locationId: params.locationId, memberId: response.data.member_id, paymentMethodId: response.data.id },
        ],
      });

      setTimeout(() => {
        queryClient.refetchQueries({
          queryKey: ['payment-methods', { locationId: params.locationId, memberId: response.data.member_id }],
        });
      }, 3000);
    },
  });

const updatePaymentMethodV2 = <TPayload>(
  { locationId, memberId, paymentMethodId }: { locationId: number; memberId: number; paymentMethodId: number },
  request: TPayload,
) => {
  const config = getConfig();

  return axiosInstance
    .put<
      APIResponse<PaymentMethod>
    >(`${config.domains.membersV2}/members/locations/${locationId}/members/${memberId}/payment-methods/${paymentMethodId}`, request)
    .then(({ data }) => data);
};
export const useUpdatePaymentMethodV2Mutation = (params: { locationId: number }) =>
  useMutation({
    mutationFn: ({ request, paymentMethodId }: { request: UpdateCCPaymentPayload; paymentMethodId: number }) =>
      updatePaymentMethodV2({ ...params, memberId: request.member_id, paymentMethodId }, request),
    onSuccess: async (response: APIResponse<PaymentMethod>) => {
      toast.success(response.message);

      const queryClient = getQueryClient();

      queryClient.invalidateQueries({
        queryKey: ['payment-methodsV2', { locationId: params.locationId, memberId: response.data.member_id }],
      });

      queryClient.invalidateQueries({
        queryKey: [
          'payment-methodV2',
          { locationId: params.locationId, memberId: response.data.member_id, paymentMethodId: response.data.id },
        ],
      });
    },
  });

export const useSetAsPrimaryPaymentMethodV2Mutation = (params: { locationId: number }) =>
  useMutation({
    mutationFn: ({ request, paymentMethodId }: { request: SetAsPrimaryPaymentPayload; paymentMethodId: number }) =>
      updatePaymentMethodV2({ ...params, memberId: request.member_id, paymentMethodId }, request),
  });

const deletePaymentMethodV2 = ({
  locationId,
  memberId,
  paymentMethodId,
}: {
  locationId: number;
  memberId: number;
  paymentMethodId: number;
}) => {
  const config = getConfig();

  return axiosInstance
    .delete<APIResponse>(
      `${config.domains.membersV2}/members/locations/${locationId}/members/${memberId}/payment-methods/${paymentMethodId}`,
    )
    .then(({ data }) => data);
};
export const useDeletePaymentMethodV2Mutation = (params: {
  locationId: number;
  memberId: number;
  paymentMethodId: number;
}) =>
  useMutation({
    mutationFn: () => deletePaymentMethodV2({ ...params }),
    onSuccess: async (response: APIResponse) => {
      const queryClient = getQueryClient();

      await queryClient.refetchQueries({
        queryKey: ['payment-methodsV2', { locationId: params.locationId, memberId: params.memberId }],
      });
      toast.success(response.message);
    },
  });

const createCCPaymentV2 = (
  { locationId, memberId }: { locationId: number; memberId: number },
  request: CreatePreCCPaymentPayload,
) => {
  const config = getConfig();

  return axiosInstance
    .post<
      APIResponse<CreatePreCCPaymentResponse>
    >(`${config.domains.membersV2}/members/locations/${locationId}/members/${memberId}/payment-methods/card`, request)
    .then((data) => data?.data);
};
export const useCreateCCPaymentV2Mutation = (params: { locationId: number }) =>
  useMutation({
    mutationFn: (request: CreatePreCCPaymentPayload) =>
      createCCPaymentV2({ ...params, memberId: request.member_id }, request),
    onSuccess: async (response: APIResponse<CreatePreCCPaymentResponse>) => {
      toast.success(response.message);

      const queryClient = getQueryClient();

      queryClient.invalidateQueries({
        queryKey: ['payment-methodsV2', { locationId: params.locationId, memberId: response.data.member_id }],
      });

      queryClient.invalidateQueries({
        queryKey: [
          'payment-methodV2',
          { locationId: params.locationId, memberId: response.data.member_id, paymentMethodId: response.data.id },
        ],
      });
    },
  });

const createACHPaymentV2 = (
  { locationId, memberId }: { locationId: number; memberId: number },
  request: CreateACHPaymentPayload,
) => {
  const config = getConfig();

  return axiosInstance
    .post<
      APIResponse<PaymentMethod>
    >(`${config.domains.membersV2}/members/locations/${locationId}/members/${memberId}/payment-methods/ach`, request)
    .then(({ data }) => data);
};
export const useCreateACHPaymentV2Mutation = (params: { locationId: number }) =>
  useMutation({
    mutationFn: (request: CreateACHPaymentPayload) =>
      createACHPaymentV2({ ...params, memberId: request.member_id }, request),
    onSettled: async (response, error, variables) => {
      if (error || !response || response?.status !== 'CREATED') return;

      const queryClient = getQueryClient();

      await queryClient.cancelQueries({
        queryKey: ['payment-methodsV2', { locationId: response.data.location_id, memberId: response.data.member_id }],
      });

      queryClient.setQueryData(
        ['payment-methodsV2', { locationId: response.data.location_id, memberId: response.data.member_id }],
        (currentPaymentMethodList: PaymentMethod[]) => {
          return currentPaymentMethodList
            .map((list) => ({ ...list, default: false }))
            .concat({
              ...response.data,
              default: true,
              last_4_digits_account: variables.account_ach.substring(variables.account_ach.length - 4),
              last_4_digits_routing: variables.routing_ach.substring(variables.routing_ach.length - 4),
            });
        },
      );
    },
    onSuccess: async (response: APIResponse<PaymentMethod>) => {
      toast.success(response.message);

      const queryClient = getQueryClient();

      queryClient.invalidateQueries({
        queryKey: ['payment-methodsV2', { locationId: params.locationId, memberId: response.data.member_id }],
      });

      queryClient.invalidateQueries({
        queryKey: [
          'payment-methodV2',
          { locationId: params.locationId, memberId: response.data.member_id, paymentMethodId: response.data.id },
        ],
      });
    },
  });

const checkoutPayment = (
  { locationId, memberId, paymentId }: { locationId: number; memberId: number; paymentId: number },
  payment: any,
) => {
  const config = getConfig();

  return axiosInstance.put<APIResponse>(
    `${config.domains.members}/members/locations/${locationId}/members/${memberId}/payments/${paymentId}`,
    (({
      account_type,
      bank_account_number,
      card_number,
      default_payment_method,
      expiration_date,
      payment_method,
      routing_number,
      security_code,
      ...clean
    }) => clean)(payment),
  );
};
export const useCheckoutPaymentMutation: any = (params: { locationId: number; memberId: number }) =>
  useMutation({
    mutationFn: (payment: any) => checkoutPayment({ ...params, paymentId: payment.payment_id }, payment),
    onSuccess: async (response: any) => {
      const queryClient = getQueryClient();

      await queryClient.refetchQueries({ queryKey: ['payments', params] });
      toast.success(response.data.message);
    },
  });

const refundPayment = (
  { locationId, memberId, paymentId }: { locationId: number; memberId: number; paymentId: number },
  payment: any,
) => {
  const config = getConfig();

  return axiosInstance.post<APIResponse>(
    `${config.domains.members}/members/locations/${locationId}/members/${memberId}/payments/${paymentId}/refunds`,
    payment,
  );
};
export const useRefundPaymentMutation: any = (params: { locationId: number; memberId: number; paymentId: number }) =>
  useMutation({
    mutationFn: (payment: any) => refundPayment(params, payment),
    onSuccess: async (response: any) => {
      const queryClient = getQueryClient();

      await queryClient.refetchQueries({
        queryKey: ['payments', { locationId: params.locationId, memberId: params.memberId }],
      });
      await queryClient.refetchQueries({
        queryKey: ['payments-history', { locationId: params.locationId, memberId: params.memberId }],
      });
      await queryClient.refetchQueries({
        queryKey: ['activityHistory', params.locationId, params.memberId],
      });

      toast.success(response.data.message);
    },
  });

const takePayment = ({ locationId, memberId }: { locationId: number; memberId: number }, payment: any) => {
  const config = getConfig();

  return axiosInstance
    .post<
      APIResponse<{ member_id: number }>
    >(`${config.domains.members}/members/locations/${locationId}/members/${memberId}/payments`, payment)
    .then((data) => data?.data);
};
export const useTakePaymentMutation = (params: { locationId: number }) =>
  useMutation({
    mutationFn: (payment: any) => takePayment({ ...params, memberId: payment.member_id }, payment),
    onSuccess: async (response) => {
      const queryClient = getQueryClient();

      await queryClient.refetchQueries({
        queryKey: ['payments-history', { locationId: params.locationId, memberId: response.data.member_id }],
      });

      await queryClient.refetchQueries({
        queryKey: ['payments', { locationId: params.locationId, memberId: response.data.member_id }],
      });

      await queryClient.refetchQueries({
        queryKey: ['activityHistory', params.locationId, response.data.member_id],
      });

      toast.success(response.message);
    },
  });

export const getPayments = ({ locationId, memberId }: { locationId: number; memberId: number }) => {
  const config = getConfig();

  return axiosInstance
    .get<APIResponse>(`${config.domains.members}/members/locations/${locationId}/members/${memberId}/payments`)
    .then(({ data }) => data?.data);
};
export const useGetPaymentsQuery: any = (params: { locationId: number; memberId: number }) =>
  useQuery({ queryKey: ['payments', params], queryFn: () => getPayments(params) });

export const getPaymentsHistory = ({ locationId, memberId }: { locationId: number; memberId: number }) => {
  const config = getConfig();

  return axiosInstance
    .get<
      APIResponse<PaymentHistory[]>
    >(`${config.domains.members}/members/locations/${locationId}/members/${memberId}/payments/history`)
    .then(({ data }) => data?.data);
};
export const useGetPaymentsHistoryQuery = (params: { locationId: number; memberId: number }) =>
  useQuery({ queryKey: ['payments-history', params], queryFn: () => getPaymentsHistory(params) });

export const getPaymentsSchedule = ({ locationId, memberId }: { locationId: number; memberId: number }) => {
  const config = getConfig();

  return axiosInstance
    .get<APIResponse>(`${config.domains.members}/members/locations/${locationId}/members/${memberId}/payments/schedule`)
    .then(({ data }) => data?.data);
};
export const useGetPaymentsScheduleQuery: any = (params: { locationId: number; memberId: number }) =>
  useQuery({ queryKey: ['payments-schedule', params], queryFn: () => getPaymentsSchedule(params) });

const updateScheduledPayment = (
  { locationId, memberId, paymentId }: { locationId: number; memberId: number; paymentId: number },
  payment: any,
) => {
  const config = getConfig();

  return axiosInstance.put<APIResponse>(
    `${config.domains.members}/members/locations/${locationId}/members/${memberId}/payments/schedule/${paymentId}`,
    payment,
  );
};
export const useUpdateScheduledPaymentMutation: any = (params: {
  locationId: number;
  memberId: number;
  paymentId: number;
}) =>
  useMutation({
    mutationFn: (payment: any) => updateScheduledPayment(params, payment),
    onSuccess: async (response: any) => {
      const queryClient = getQueryClient();

      await queryClient.refetchQueries({ queryKey: ['payments-schedule', params] });
      toast.success(response.data.message);
    },
  });

export const getPaymentReceipt = ({
  locationId,
  memberId,
  paymentId,
}: {
  locationId: number;
  memberId: number;
  paymentId: number;
}) => {
  const config = getConfig();

  return axiosInstance
    .get<APIResponse>(
      `${config.domains.members}/members/locations/${locationId}/members/${memberId}/payments/${paymentId}/receipt`,
      {
        responseType: 'blob',
      },
    )
    .then((response) => response.request.response);
};

export const useGetPaymentReceiptQuery: any = (params: {
  locationId: number;
  memberId: number;
  paymentId: number;
  downloadFile: boolean;
}) =>
  useQuery({
    queryKey: ['receipt', params],
    queryFn: () => getPaymentReceipt((({ downloadFile, ...clean }) => clean)(params)),
    enabled: !!params.paymentId && params.downloadFile,
  });

export const getPayment = ({
  locationId,
  memberId,
  paymentId,
}: {
  locationId: number;
  memberId: number;
  paymentId: number;
}) => {
  const config = getConfig();

  return axiosInstance
    .get<APIResponse>(
      `${config.domains.members}/members/locations/${locationId}/members/${memberId}/payments/${paymentId}`,
    )
    .then(({ data }) => data?.data);
};

export const useGetPaymentQuery: any = (params: {
  locationId: number;
  memberId: number;
  paymentId: number;
  execute: boolean;
}) =>
  useQuery({
    queryKey: ['payments', params],
    queryFn: () => getPayment((({ execute, ...clean }) => clean)(params)),
    enabled: !!params.paymentId && params.execute,
  });

const updatePayment = (
  { locationId, memberId, paymentId }: { locationId: number; memberId: number; paymentId: number },
  payment: any,
) => {
  const config = getConfig();

  return axiosInstance.put<APIResponse>(
    `${config.domains.members}/members/locations/${locationId}/members/${memberId}/payments/${paymentId}`,
    payment,
  );
};
export const useUpdatePaymentMutation: any = (params: { locationId: number; memberId: number; paymentId: number }) =>
  useMutation({
    mutationFn: (payment: any) => updatePayment(params, payment),
    onSuccess: async (response: any) => {
      const queryClient = getQueryClient();

      await queryClient.refetchQueries({ queryKey: ['payments', params] });
      await queryClient.refetchQueries({ queryKey: ['memberships', params] });
      toast.success(response.data.message);
    },
  });

export const checkPaymentStatus = ({
  locationId,
  memberId,
  paymentId,
}: {
  locationId: number;
  memberId: number;
  paymentId: number;
}) => {
  const config = getConfig();

  return axiosInstance
    .get<APIResponse>(
      `${config.domains.members}/members/locations/${locationId}/members/${memberId}/payments/${paymentId}`,
    )
    .then(({ data }) => data?.data);
};
export const useCheckPaymentStatusQuery: any = (params: { locationId: number; memberId: number; paymentId: number }) =>
  useQuery({ queryKey: ['payments', params], queryFn: () => checkPaymentStatus(params) });

const checkoutPaymentV2 = (
  { locationId, memberId, paymentId }: { locationId: number; memberId: number; paymentId: number },
  payment: any,
) => {
  const config = getConfig();

  return axiosInstance.put<APIResponse>(
    `${config.domains.membersV2}/members/locations/${locationId}/members/${memberId}/payments/${paymentId}`,
    (({
      account_type,
      bank_account_number,
      card_number,
      default_payment_method,
      expiration_date,
      payment_method,
      routing_number,
      security_code,
      ...clean
    }) => clean)(payment),
  );
};
export const useCheckoutPaymentV2Mutation: any = (params: { locationId: number; memberId: number }) =>
  useMutation({
    mutationFn: (payment: any) => checkoutPaymentV2({ ...params, paymentId: payment.payment_id }, payment),
    onSuccess: async (response: any) => {
      const queryClient = getQueryClient();

      await queryClient.refetchQueries({ queryKey: ['paymentsV2', params] });
      await queryClient.refetchQueries({
        queryKey: ['activityHistory', params.locationId, params.memberId],
      });
      toast.success(response.data.message);
    },
  });

const refundPaymentV2 = (
  { locationId, memberId, paymentId }: { locationId: number; memberId: number; paymentId: number },
  payment: any,
) => {
  const config = getConfig();

  return axiosInstance.post<APIResponse>(
    `${config.domains.membersV2}/members/locations/${locationId}/members/${memberId}/payments/${paymentId}/refunds`,
    payment,
  );
};
export const useRefundPaymentV2Mutation = (params: { locationId: number; memberId: number; paymentId: number }) =>
  useMutation({
    mutationFn: (payment: any) => refundPaymentV2(params, payment),
    onSuccess: async () => {
      const queryClient = getQueryClient();

      await queryClient.invalidateQueries({
        queryKey: ['paymentsV2', { locationId: params.locationId, memberId: params.memberId }],
      });

      await queryClient.invalidateQueries({ queryKey: ['paymentV2', params] });

      await queryClient.refetchQueries({
        queryKey: ['activityHistory', params.locationId, params.memberId],
      });

      // toast.success(response.data.message);
    },
  });
export const retryPaymentV2 = (
  { locationId, memberId, paymentId }: { locationId: number; memberId: number; paymentId: number | null },
  payment: any,
) => {
  const config = getConfig();
  return axiosInstance.post<APIResponse>(
    `${config.domains.membersV2}/members/locations/${locationId}/members/${memberId}/payments/${paymentId}/retries`,
    payment,
  );
};

export const useRetryPaymentV2Mutation = (params: { locationId: number; memberId: number; paymentId: number }) =>
  useMutation({
    mutationFn: (payment: any) => retryPaymentV2(params, payment),
    onSuccess: async () => {
      const queryClient = getQueryClient();
      await queryClient.refetchQueries({ queryKey: ['payments', params] });
      await queryClient.refetchQueries({
        queryKey: ['paymentsV2', { locationId: params.locationId, memberId: params.memberId }],
      });
      await queryClient.refetchQueries({
        queryKey: ['activityHistory', params.locationId, params.memberId],
      });
    },
  });

const takePaymentV2 = (
  { locationId, memberId }: { locationId: number; memberId: number },
  payment: TakePaymentV2Payload,
) => {
  const config = getConfig();

  return axiosInstance
    .post<
      APIResponse<TakePaymentV2Response>
    >(`${config.domains.membersV2}/members/locations/${locationId}/members/${memberId}/payments`, payment)
    .then(({ data }) => data);
};
export const useTakePaymentV2Mutation = (params: { locationId: number }) =>
  useMutation({
    mutationFn: (payment: TakePaymentV2Payload) => takePaymentV2({ ...params, memberId: payment.member_id }, payment),
    onSuccess: async (response: APIResponse<TakePaymentV2Response>) => {
      const queryClient = getQueryClient();

      // TODO: Remove this setTimeout after BE sends the recently created payment to the FE
      setTimeout(async () => {
        await queryClient.refetchQueries({
          queryKey: ['paymentsV2', { locationId: params.locationId, memberId: response.data.member_id }],
        });
      }, 5000);

      await queryClient.refetchQueries({
        queryKey: ['memberV2', params],
      });

      await queryClient.refetchQueries({
        queryKey: ['activityHistory', params.locationId, response.data.member_id],
      });

      toast.success(response.message);
    },
  });

export const getPaymentsV2 = ({ locationId, memberId }: { locationId: number; memberId: number }) => {
  const config = getConfig();

  return axiosInstance
    .get<
      APIResponse<PaymentHistoryV2[]>
    >(`${config.domains.membersV2}/members/locations/${locationId}/members/${memberId}/payments`)
    .then(({ data }) => data?.data);
};

export const getActivityHistory = ({ locationId, memberId }: { locationId: number; memberId: number }) => {
  const config = getConfig();

  return axiosInstance
    .get<
      APIResponse<ActivityHistory[]>
    >(`${config.domains.membersV2}/members/locations/${locationId}/members/${memberId}/activity-history`)
    .then(({ data }) => {
      return data?.data;
    });
};

export const useGetPaymentsV2Query = (params: { locationId: number; memberId: number | undefined }) =>
  useQuery({
    queryKey: ['paymentsV2', { locationId: params.locationId, memberId: params.memberId }],
    queryFn: () => {
      if (!params.memberId) {
        throw new Error('memberId is required for getPaymentsV2');
      }
      return getPaymentsV2({ locationId: params.locationId, memberId: params.memberId });
    },
    enabled: !!params.memberId,
  });

export const useGetActivityHistoryQuery = (params: { locationId: number; memberId: number | undefined }) =>
  useQuery({
    queryKey: ['activityHistory', params.locationId, params.memberId],
    queryFn: () => {
      if (!params.memberId) {
        throw new Error('memberId is required for getActivityHistory');
      }
      return getActivityHistory({ locationId: params.locationId, memberId: params.memberId });
    },
    enabled: !!params.memberId && !!params.locationId,
    staleTime: 0,
    gcTime: 30 * 60 * 1000,
    retry: 2,
    retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 10000),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: true,
  });

export const getPaymentV2 = ({
  locationId,
  memberId,
  paymentId,
}: {
  locationId: number;
  memberId: number;
  paymentId: number;
}) => {
  const config = getConfig();

  return axiosInstance
    .get<
      APIResponse<PaymentDetail>
    >(`${config.domains.membersV2}/members/locations/${locationId}/members/${memberId}/payments/${paymentId}`)
    .then(({ data }) => data?.data);
};

export const useGetPaymentV2Query = (params: {
  locationId: number;
  memberId: number | undefined;
  paymentId: number | undefined;
  execute: boolean;
}) =>
  useQuery({
    queryKey: ['paymentV2', { locationId: params.locationId, memberId: params.memberId, paymentId: params.paymentId }],
    queryFn: () => {
      const { execute, ...rest } = params;
      return getPaymentV2({ ...rest, paymentId: params.paymentId!, memberId: params.memberId! });
    },
    enabled: !!params.paymentId && !!params.memberId && params.execute,
  });

const updatePaymentV2 = (
  {
    locationId,
    memberId,
    paymentId,
    updateType,
  }: { locationId: number; memberId: number; paymentId: number; updateType?: UpdatePaymentTypeOptions },
  payment: EditPaymentV2Payload | UpdatePaymentV2StatusPayload,
) => {
  const config = getConfig();

  return axiosInstance
    .put<
      APIResponse<PaymentDetail>
    >(`${config.domains.membersV2}/members/locations/${locationId}/members/${memberId}/payments/${paymentId}${updateType ? `?update_type=${updateType}` : ''}`, payment)
    .then(({ data }) => data);
};

const updatePaymentStatusV2 = ({
  locationId,
  memberId,
  paymentId,
  paymentMethodId,
  update_type,
}: {
  locationId: number;
  memberId: number;
  paymentId: number;
  paymentMethodId: number | undefined;
  update_type: UpdatePaymentTypeOptions;
}) => {
  const config = getConfig();

  return axiosInstance
    .put<
      APIResponse<PaymentDetail>
    >(`${config.domains.membersV2}/members/locations/${locationId}/members/${memberId}/payments/${paymentId}${update_type ? `?update_type=${update_type}` : ''}`, { location_id: locationId, member_id: memberId, payment_id: paymentId, payment_method_id: paymentMethodId })
    .then(({ data }) => data);
};

export const useUpdatePaymentStatusV2Mutation = (params: {
  locationId: number | undefined;
  memberId: number | undefined;
  paymentId: number | undefined;
  paymentMethodId: number | undefined;
  updateType: UpdatePaymentTypeOptions;
}) =>
  useMutation({
    mutationFn: () => {
      const { locationId, memberId, paymentId, paymentMethodId } = params;
      if (!locationId || !memberId || !paymentId) {
        throw new Error('locationId, memberId, and paymentId are required for updatePaymentStatusV2');
      }

      return updatePaymentStatusV2({
        locationId,
        memberId,
        paymentId,
        paymentMethodId,
        update_type: params.updateType,
      });
    },
    onSuccess: async (response: APIResponse<PaymentDetail>) => {
      const queryClient = getQueryClient();
      await queryClient.refetchQueries({
        queryKey: ['paymentsV2', { locationId: params.locationId, memberId: params.memberId }],
      });
      toast.success(response.message);
    },
  });

export const useUpdatePaymentV2Mutation = (params: { locationId: number; updateType?: UpdatePaymentTypeOptions }) =>
  useMutation({
    mutationFn: (payment: EditPaymentV2Payload | UpdatePaymentV2StatusPayload) => {
      if (payment.payment_id === undefined) {
        throw new Error('paymentId is required for updatePaymentV2');
      }

      return updatePaymentV2({ ...params, paymentId: payment.payment_id, memberId: payment.member_id }, payment);
    },
    onSuccess: async (response: APIResponse<PaymentDetail>) => {
      const queryClient = getQueryClient();

      const { locationId } = params;
      const memberId = response.data.member_id;

      await queryClient.refetchQueries({ queryKey: ['paymentsV2', { locationId, memberId }] });
      await queryClient.refetchQueries(membershipKeys.listV2({ locationId, memberId }));
      await queryClient.refetchQueries({
        queryKey: ['memberV2', { locationId, memberId }],
      });
      await queryClient.invalidateQueries({
        queryKey: ['paymentV2', { locationId, memberId, paymentId: response.data.payment_id }],
      });
      await queryClient.refetchQueries({
        queryKey: ['activityHistory', locationId, memberId],
      });
      toast.success(response.message);
    },
  });

export interface Reconciliation {
  processed_date: Date;
  total_amount: number;
  type: string;
  credit: boolean;
}
export const getReconciliations = ({
  locationId,
  memberId,
}: {
  locationId: number;
  memberId: number;
}): Promise<Reconciliation[]> => {
  const config = getConfig();

  return axiosInstance
    .get<
      APIResponse<Reconciliation[]>
    >(`${config.domains.membersV2}/members/locations/${locationId}/members/${memberId}/reconciliation`)
    .then(({ data }) => data?.data);
};
export const useGetReconciliationsQuery = (params: { locationId: number; memberId: number }) =>
  useQuery({
    queryKey: ['reconciliations', params],
    queryFn: () => getReconciliations(params),
    enabled: !!params.locationId && !!params.memberId,
  });

const onboardCustomer = ({ locationId, memberId }: { locationId: number; memberId: number }, request: any) => {
  const config = getConfig();

  return axiosInstance.put<APIResponse>(
    `${config.domains.members}/members/locations/${locationId}/members/${memberId}/payrix_onboard`,
    request,
  );
};
export const useOnboardCustomerMutation: any = (params: { locationId: number }) =>
  useMutation({
    mutationFn: (request: any) =>
      onboardCustomer({ ...params, memberId: request.memberId }, (({ memberId, ...clean }) => clean)(request)),
    onSuccess: async (response: any) => {
      const queryClient = getQueryClient();
      await queryClient.refetchQueries({ queryKey: ['payrix_onboard', params] });
      toast.success(response.data.message);
    },
  });

const onboardCustomerV2 = ({ locationId, memberId }: { locationId: number; memberId: number }, request: any) => {
  const config = getConfig();

  return axiosInstance.put<APIResponse>(
    `${config.domains.membersV2}/members/locations/${locationId}/members/${memberId}/payrix`,
    request,
  );
};
export const useOnboardCustomerV2Mutation: any = (params: { locationId: number }) =>
  useMutation({
    mutationFn: (request: any) =>
      onboardCustomerV2({ ...params, memberId: request.memberId }, (({ memberId, ...clean }) => clean)(request)),
    onSuccess: async (response: any) => {
      const queryClient = getQueryClient();
      await queryClient.refetchQueries({ queryKey: ['payrix_onboardV2', params] });
      toast.success(response.data.message);
    },
  });

const payrixTxnUpdates = (request: any) => {
  const config = getConfig();

  return axiosInstance.post<APIResponse>(`${config.domains.membersV2}/members/payrix_txn_updates`, request);
};
export const usePayrixTxnUpdatesMutation: any = () =>
  useMutation({
    mutationFn: (request: any) => payrixTxnUpdates(request),
    onSuccess: async (response: any) => {
      const queryClient = getQueryClient();
      await queryClient.refetchQueries({ queryKey: ['payrix_txn_updates'] });
      toast.success(response.data.message);
    },
  });

export const verificationCode = ({ member_email }: { member_email: string }) => {
  const config = getConfig();

  return axiosInstance
    .get<APIResponse>(`${config.domains.members}/members/verification_code/${member_email}`)
    .then(({ data }) => data?.data);
};
export const useVerificationCodeQuery: any = (params: { member_email: string }) =>
  useQuery({ queryKey: ['verification_code', params], queryFn: () => verificationCode(params) });

const verify = (request: any) => {
  const config = getConfig();

  return axiosInstance.post<APIResponse>(`${config.domains.membersV2}/members/verify`, request);
};
export const useVerifyMutation: any = () =>
  useMutation({
    mutationFn: (request: any) => verify(request),
    onSuccess: async (response: any) => {
      const queryClient = getQueryClient();
      await queryClient.refetchQueries({ queryKey: ['verify'] });
      toast.success(response.data.message);
    },
  });

const getMember = ({ locationId, memberId }: { locationId: number; memberId: number }) => {
  const config = getConfig();

  return axiosInstance
    .get<APIResponse<Contact>>(`${config.domains.members}/members/locations/${locationId}/members/${memberId}`)
    .then(({ data }) => data?.data);
};

export const useGetMemberQuery: any = (params: { locationId: number; memberId: number | undefined }) =>
  useQuery({
    queryKey: ['member', params],
    queryFn: () => getMember({ ...params, memberId: params.memberId! }),
    enabled: !!params.locationId && !!params.memberId,
  });

const getMemberV2 = ({ locationId, memberId }: { locationId: number; memberId: number }) => {
  const config = getConfig();

  return axiosInstance
    .get<APIResponse<Contact>>(`${config.domains.membersV2}/members/locations/${locationId}/members/${memberId}`)
    .then(({ data }) => data?.data);
};

export const useGetMemberV2Query = (params: { locationId: number; memberId: number }) =>
  useQuery({
    queryKey: ['memberV2', params],
    queryFn: () => getMemberV2(params),
    enabled: !!params.locationId && !!params.memberId,
  });

export const getMemberships = ({
  locationId,
  memberId,
}: {
  locationId: number;
  memberId: number;
}): Promise<Membership[]> => {
  const config = getConfig();

  return axiosInstance
    .get<
      APIResponse<Membership[]>
    >(`${config.domains.members}/members/locations/${locationId}/members/${memberId}/plans`)
    .then(({ data }) => data?.data);
};

export const useGetMembershipsQuery: any = (params: { locationId: number; memberId: number }) =>
  useQuery({
    queryKey: ['memberships', params],
    queryFn: () => getMemberships(params),
  });

const createMembership = ({ locationId, memberId }: { locationId: number; memberId: number }, membership: any) => {
  const config = getConfig();

  return axiosInstance.post<APIResponse>(
    `${config.domains.members}/members/locations/${locationId}/members/${memberId}/plans`,
    (({ discount_is_percentage, duration, payment_amount, ...clean }) => clean)({
      ...membership,
    }),
  );
};
export const useCreateMembershipMutation: any = (params: { locationId: number; memberId: number }) =>
  useMutation({
    mutationFn: (membership: any) => createMembership(params, membership),
    onSuccess: async (response: any) => {
      const queryClient = getQueryClient();

      await queryClient.refetchQueries({
        queryKey: ['memberships', { locationId: params.locationId, memberId: params.memberId }],
      });
      toast.success(response.data.message);
    },
  });

const simulateMembershipCreation = (
  { locationId, memberId }: { locationId: number; memberId: number },
  membership: any,
) => {
  const config = getConfig();

  return axiosInstance.post<APIResponse<CreateSimulatedMembershipResponse>>(
    `${config.domains.membersV2}/members/locations/${locationId}/members/${memberId}/memberships`,
    (({ discount_is_percentage, duration, payment_amount, ...clean }) => clean)({
      ...membership,
    }),
  );
};

export const useSimulateMembershipCreationMutation = (params: { locationId: number; memberId: number }) =>
  useMutation({
    mutationFn: (membership: CreateSimulatedMembershipPayload) => simulateMembershipCreation(params, membership),
    onSuccess: async () => {
      const queryClient = getQueryClient();

      await queryClient.refetchQueries({
        queryKey: ['simulateMembership', { locationId: params.locationId, memberId: params.memberId }],
      });
    },
  });

export const useCreateMembershipOptimisticMutation: any = (params: { locationId: number; memberId: number }) =>
  useMutation({
    mutationFn: (membership: any) => createMembership(params, membership),
    onMutate: async (newMembership: any) => {
      const queryClient = getQueryClient();
      await queryClient.cancelQueries({ queryKey: ['memberships'] });

      const previousMemberships = queryClient.getQueryData(['memberships']);

      await queryClient.setQueryData(['memberships', params], (oldData?: any[]) => {
        const localNewMembership = structuredClone(newMembership);
        localNewMembership.preview = true;
        localNewMembership.membership_id = `preview-${Math.random()}`;
        localNewMembership.start_date = localNewMembership.plan_start_date;
        localNewMembership.membership_status = 'Preview';

        if (!oldData) return [localNewMembership];
        return [...oldData, localNewMembership];
      });

      return { previousMemberships };
    },
    onError: (_1, _2, context: any) => {
      const queryClient = getQueryClient();
      queryClient.setQueryData(['memberships', params], context?.previousMemberships);
    },
    onSettled: async () => {
      const queryClient = getQueryClient();
      await queryClient.invalidateQueries({
        queryKey: ['memberships'],
      });
    },
  });

const updateMembership = (
  { locationId, memberId, membershipId }: { locationId: number; memberId: number; membershipId: number },
  membership: any,
) => {
  const config = getConfig();

  return axiosInstance.put<APIResponse>(
    `${config.domains.members}/members/locations/${locationId}/members/${memberId}/plans/${membershipId}`,
    (({ apply_discount_to_all_payments, discount_is_percentage, duration, payment_amount, ...clean }) => clean)({
      ...membership,
    }),
  );
};
export const useUpdateMembershipMutation: any = (params: {
  locationId: number;
  memberId: number;
  membershipId: number;
}) =>
  useMutation({
    mutationFn: (membership: any) => updateMembership(params, membership),
    onSuccess: async (response: any) => {
      const queryClient = getQueryClient();

      await queryClient.refetchQueries({
        queryKey: ['memberships', { locationId: params.locationId, memberId: params.memberId }],
      });
      toast.success(response.data.message);
    },
  });

const addInvoiceWriteoff = (
  { locationId, memberId, invoiceId }: { locationId: number; memberId: number; invoiceId: number },
  sessions: any,
) => {
  const config = getConfig();

  return axiosInstance.post<APIResponse>(
    `${config.domains.membersV2}/members/locations/${locationId}/members/${memberId}/invoice/${invoiceId}/writeoff`,
    sessions,
  );
};
export const useAddInvoiceWriteoffMutation: any = (params: {
  locationId: number;
  memberId: number;
  invoiceId: number;
}) =>
  useMutation({
    mutationFn: (sessions: any) => addInvoiceWriteoff(params, sessions),
    onSuccess: async (response: any) => {
      const queryClient = getQueryClient();

      await queryClient.refetchQueries({
        queryKey: ['invoices', { locationId: params.locationId, memberId: params.memberId }],
      });
      await queryClient.refetchQueries({
        queryKey: ['InvoiceWriteoff', params],
      });
      toast.success(response.data.message);
    },
  });

export const getInvoices = ({
  locationId,
  memberId,
  membershipId,
  nextInvoice,
}: {
  locationId: number;
  memberId: number;
  membershipId?: number;
  nextInvoice?: boolean;
}): Promise<Membership[]> => {
  const config = getConfig();

  return axiosInstance
    .get<
      APIResponse<Membership[]>
    >(`${config.domains.membersV2}/members/locations/${locationId}/members/${memberId}/invoices${membershipId || nextInvoice !== undefined ? `?membership_id=${membershipId}&next_invoice=${Boolean(nextInvoice)}` : ''}`)
    .then(({ data }) => data?.data);
};

export const useGetInvoicesQuery: any = (params: {
  locationId: number;
  memberId: number;
  membershipId?: number;
  nextInvoice?: boolean;
}) =>
  useQuery({
    queryKey: ['invoices', params],
    queryFn: () => getInvoices(params),
  });

export const getInvoice = ({
  locationId,
  memberId,
  invoiceId,
}: {
  locationId: number;
  memberId: number;
  invoiceId: number;
}): Promise<Membership[]> => {
  const config = getConfig();

  return axiosInstance
    .get<
      APIResponse<Membership[]>
    >(`${config.domains.membersV2}/members/locations/${locationId}/members/${memberId}/invoices/${invoiceId}`)
    .then(({ data }) => data?.data);
};

export const useGetInvoiceQuery: any = (params: { locationId: number; memberId: number; invoiceId: number }) =>
  useQuery({
    queryKey: ['invoice', params],
    queryFn: () => getInvoice(params),
  });

const addInvoiceCreditmemo = (
  { locationId, memberId }: { locationId: number; memberId: number },
  payload: {
    invoice_id?: number;
    notes?: string;
    total_amount?: number;
  },
) => {
  const config = getConfig();

  return axiosInstance.post<APIResponse>(
    `${config.domains.membersV2}/members/locations/${locationId}/members/${memberId}/invoices/creditmemo`,
    {
      ...payload,
      member_id: memberId,
      location_id: locationId,
    },
  );
};

const updateInvoiceStatus = (
  { locationId, memberId }: { locationId: number; memberId: number },
  payload: {
    invoice_id?: number;
    invoice_status_type_id?: number;
    notes?: string;
  },
) => {
  const config = getConfig();

  return axiosInstance.put<APIResponse>(
    `${config.domains.membersV2}/members/locations/${locationId}/members/${memberId}/invoices/${payload.invoice_id}`,
    {
      ...payload,
      member_id: memberId,
      location_id: locationId,
    },
  );
};

export const useUpdateInvoiceStatusMutation: any = (params: { locationId: number; memberId: number }) =>
  useMutation({
    mutationFn: (payload: any) => updateInvoiceStatus(params, payload),
    onSuccess: async (response: any) => {
      const queryClient = getQueryClient();

      await queryClient.refetchQueries({
        queryKey: ['invoices', { locationId: params.locationId, memberId: params.memberId }],
      });
      toast.success(response.data.message);
    },
  });

export const useAddInvoiceCreditmemoMutation: any = (params: { locationId: number; memberId: number }) =>
  useMutation({
    mutationFn: (payload: any) => addInvoiceCreditmemo(params, payload),
    onSuccess: async (response: any) => {
      const queryClient = getQueryClient();

      await queryClient.refetchQueries({
        queryKey: ['invoices', { locationId: params.locationId, memberId: params.memberId }],
      });
      toast.success(response.data.message);
    },
  });

const createInvoice = ({ locationId }: { locationId: number }, payload: CreateInvoicePayload) => {
  const config = getConfig();

  return axiosInstance
    .post<APIResponse<Invoice>>(
      `${config.domains.membersV2}/members/locations/${locationId}/members/${payload.member_id}/invoices`,
      {
        ...payload,
        member_id: payload.member_id,
        location_id: locationId,
      },
    )
    .then(({ data }) => data);
};

export const useCreateInvoiceMutation = (params: { locationId: number }) =>
  useMutation({
    mutationFn: (payload: CreateInvoicePayload) => createInvoice(params, payload),
    onSuccess: async (response: APIResponse<Invoice>) => {
      const queryClient = getQueryClient();

      await queryClient.refetchQueries({
        queryKey: ['invoices', { locationId: params.locationId, memberId: response.data.member_id }],
      });

      await queryClient.refetchQueries({
        queryKey: ['memberV2', { locationId: params.locationId, memberId: response.data.member_id }],
      });

      await queryClient.invalidateQueries({
        queryKey: ['activityHistory', params.locationId, response.data.member_id],
      });

      toast.success(response.message);
    },
  });

const addSessions = (
  { locationId, memberId, membershipId }: { locationId: number; memberId: number; membershipId: number },
  sessions: any,
) => {
  const config = getConfig();

  return axiosInstance.post<APIResponse>(
    `${config.domains.members}/members/locations/${locationId}/members/${memberId}/plans/${membershipId}/sessions`,
    sessions,
  );
};
export const useAddSessionsMutation: any = (params: { locationId: number; memberId: number; membershipId: number }) =>
  useMutation({
    mutationFn: (sessions: any) => addSessions(params, sessions),
    onSuccess: async (response: any) => {
      const queryClient = getQueryClient();

      await queryClient.refetchQueries({
        queryKey: ['memberships', { locationId: params.locationId, memberId: params.memberId }],
      });
      await queryClient.refetchQueries({
        queryKey: ['sessions', params],
      });
      toast.success(response.data.message);
    },
  });

export const getSessions = ({
  locationId,
  memberId,
  membershipId,
}: {
  locationId: number;
  memberId: number;
  membershipId: number;
}) => {
  const config = getConfig();

  return axiosInstance
    .get<APIResponse>(
      `${config.domains.members}/members/locations/${locationId}/members/${memberId}/plans/${membershipId}/sessions`,
    )
    .then(({ data }) => data?.data);
};

export const useGetSessionsQuery: any = (params: { locationId: number; memberId: number; membershipId: number }) =>
  useQuery({
    queryKey: ['sessions', params],
    queryFn: () => getSessions(params),
    enabled: !!params.membershipId && /^-?\d+$/.test(params.membershipId + ''),
  });

export const getFirstPayment = ({
  locationId,
  memberId,
  membershipId,
}: {
  locationId: number;
  memberId: number;
  membershipId: string;
}) => {
  const config = getConfig();

  return axiosInstance
    .get<APIResponse>(
      `${config.domains.members}/members/locations/${locationId}/members/${memberId}/payments?membership_id=${membershipId}&payment_number=1`,
    )
    .then(({ data }) => data?.data);
};

export const useGetFirstPaymentQuery: any = (params: { locationId: number; memberId: number; membershipId: string }) =>
  useQuery({ queryKey: ['first-payment', params], queryFn: () => getFirstPayment(params) });

export const getMemberProfile = ({ locationId, memberId }: { locationId: number; memberId: number }) => {
  const config = getConfig();

  return axiosInstance
    .get<
      APIResponse<MemberProfile>
    >(`${config.domains.members}/members/locations/${locationId}/members/${memberId}/profile`)
    .then(({ data }) => data?.data);
};

export const useGetMemberProfileQuery: any = (params: { locationId: number; memberId: number }) =>
  useQuery({
    queryKey: ['member', { ...params, type: 'profile' }],
    queryFn: () => getMemberProfile(params),
    enabled: !!params.locationId && !!params.memberId,
  });

export const getMemberProfileV2 = ({ locationId, memberId }: { locationId: number; memberId: number }) => {
  const config = getConfig();

  return axiosInstance
    .get<
      APIResponse<MemberProfile>
    >(`${config.domains.membersV2}/members/locations/${locationId}/members/${memberId}/profile`)
    .then(({ data }) => data?.data);
};

export const useGetMemberProfileV2Query: any = (params: { locationId: number; memberId: number }) =>
  useQuery({
    queryKey: ['memberV2', { ...params, type: 'profile' }],
    queryFn: () => getMemberProfileV2(params),
    enabled: !!params.locationId && !!params.memberId,
  });

const updateMemberProfile = (
  { locationId, memberId }: { locationId: number | undefined; memberId: number | undefined },
  memberProfile: Partial<MemberProfile>,
) => {
  const config = getConfig();

  return axiosInstance.put<APIResponse>(
    `${config.domains.members}/members/locations/${locationId}/members/${memberId}/profile`,
    memberProfile,
  );
};
export const useUpdateMemberProfileMutation = (params: {
  locationId: number | undefined;
  memberId: number | undefined;
}) =>
  useMutation({
    mutationFn: (memberProfile: Partial<MemberProfile>) => updateMemberProfile(params, memberProfile),
    onSuccess: async (response: any) => {
      const queryClient = getQueryClient();

      await queryClient.refetchQueries({ queryKey: ['members', { locationId: params.locationId }] });
      await queryClient.refetchQueries({ queryKey: ['member', { ...params, type: 'profile' }] });
      toast.success(response.data.message);
    },
  });

const updateMemberProfileV2 = (
  { locationId, memberId }: { locationId: number; memberId: number },
  memberProfile: MemberProfile,
) => {
  const config = getConfig();

  return axiosInstance.put<APIResponse>(
    `${config.domains.membersV2}/members/locations/${locationId}/members/${memberId}/profile`,
    memberProfile,
  );
};
export const useUpdateMemberProfileV2Mutation: any = (params: { locationId: number; memberId: number }) =>
  useMutation({
    mutationFn: (memberProfile: MemberProfile) => updateMemberProfileV2(params, memberProfile),
    onSuccess: async (response: any) => {
      const queryClient = getQueryClient();

      await queryClient.refetchQueries({ queryKey: ['membersV2', { locationId: params.locationId }] });
      await queryClient.refetchQueries({ queryKey: ['memberV2', params] });
      toast.success(response.data.message);
    },
  });

export interface FetchChangeUserAvatarArgs {
  locationId: number;
  userId: number;
}
export const fetchChangeUserAvatar = async ({ locationId, userId }: FetchChangeUserAvatarArgs, form: FormData) => {
  const config = getConfig();
  const response = await axiosInstance.put<APIResponse>(
    `${config.domains.members}/members/locations/${locationId}/members/${userId}/avatar`,
    form,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  );
  return response.data;
};

export const useChangeUserAvatarMutation: any = (params: FetchChangeUserAvatarArgs) => {
  return useMutation({
    mutationFn: (form: FormData) => fetchChangeUserAvatar(params, form),
    onSuccess: async (response: any) => {
      const queryClient = getQueryClient();

      await queryClient.refetchQueries({
        queryKey: ['member', { location: params.locationId, memberId: params.userId }],
      });
      await queryClient.refetchQueries({
        queryKey: ['user'],
      });
      toast.success(response.message);
    },
  });
};

export const getUserAvatar = async ({ locationId, userId }: Omit<FetchChangeUserAvatarArgs, 'form'>) => {
  const config = getConfig();
  const response = await axiosInstance.get<APIResponse>(
    `${config.domains.members}/members/locations/${locationId}/members/${userId}/avatar`,
  );
  return response;
};
export const useGetUserAvatarQuery: any = (params: Omit<FetchChangeUserAvatarArgs, 'form'>) =>
  useQuery({ queryKey: ['member', { ...params, type: 'profile' }], queryFn: () => getUserAvatar(params) });
